<template>
  <section class="card">
    <div class="card-title">
      <div class="question">
        <p class="subtitle-1">Question</p>
      </div>
      <p v-if="!bulkDelete" class="subtitle-1">Answer</p>
    </div>
    <div class="content">
      <slot />
    </div>
  </section>
</template>

<script>
/**
 * @fileoverview Bulk Edit/Delete Card component
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import { mapGetters } from "vuex";

import defaultValueFromType from "@/util/defaultValueFromType";
import getFieldData from "@/util/getFieldData";
import fieldMap from "@/components/config-field/field-map.json";

export default {
  name: "BulkViewCard",
  props: {
    bulkDelete: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Array,
    },
    category: {
      type: String,
    },
  },
  computed: {
    fieldsInCard() {
      let fields = [];

      for (const row of this.rows)
        for (const field of row) {
          if (field.id) {
            if (field.layout) {
              for (const layout of field.layout) {
                for (const subField of layout) {
                  if (subField.items) {
                    for (const items of subField.items) {
                      if (items.id) fields.push({ [field.id]: items });
                    }
                  } else if (subField.id) {
                    if (subField.layout) {
                      for (const sublayout of subField.layout) {
                        for (const subsubField of sublayout) {
                          if (subsubField.items) {
                            for (const items of subsubField.items) {
                              if (items.id)
                                fields.push({ [subField.id]: items });
                            }
                          } else if (subsubField.id) {
                            fields.push({ [subField.id]: subsubField });
                          }
                        }
                      }
                    } else {
                      fields.push({ [field.id]: subField });
                    }
                  }
                }
              }
            } else {
              fields.push(field);
            }
          }
          if (field.items) {
            for (const items of field.items) {
              fields.push({ [field.id]: items });
            }
          }
          if (field.fields) {
            for (const subField of field.items) {
              fields.push({ [field.id]: subField });
            }
          }
        }

      return fields;
    },
    checkboxState() {
      const fields = this.fieldsInCard;
      const fieldsHaveValue = !!fields.find((field) => {
        let parentField;
        let fieldId = field.id;
        let category = field.category;
        if (!field.id) {
          parentField = Object.keys(field)[0];
          fieldId = field[parentField].id;
          category = field[parentField].category;
        }
        return this.hasValue({
          id: fieldId,
          parentField,
          category: category || this.category,
        });
      });
      const fieldsHaveNoValue = !!fields.find((field) => {
        let parentField;
        let fieldId = field.id;
        let category = field.category;
        if (!field.id) {
          parentField = Object.keys(field)[0];
          fieldId = field[parentField].id;
          category = field[parentField].category;
        }
        return !this.hasValue({
          id: fieldId,
          parentField,
          category: category || this.category,
        });
      });
      return {
        checked: fieldsHaveValue,
        indeterminate: fieldsHaveValue && fieldsHaveNoValue,
      };
    },
    ...mapGetters({
      configTemplate: "getConfigTemplate",
      updatedValues: "getUpdatedConfigValues",
    }),
  },
  methods: {
    hasValue({ id, parentField, category = this.category }) {
      return (
        typeof this.getConfigValue({ id, parentField, category }) !==
        "undefined"
      );
    },
    getConfigValue({ id, parentField, category = this.category }) {
      const clientId = this.bulkDelete ? "bulkDelete" : "bulkEdit";
      return parentField
        ? this.updatedValues[clientId] &&
            this.updatedValues[clientId][category] &&
            this.updatedValues[clientId][category][parentField] &&
            this.updatedValues[clientId][category][parentField][id]
        : this.updatedValues[clientId] &&
            this.updatedValues[clientId][category] &&
            this.updatedValues[clientId][category][id];
    },
    fieldList(category = this.category) {
      const fieldData = this.configTemplate && this.configTemplate[category];
      return fieldData && fieldData.AvailableFieldList;
    },
    getFieldData({ id, category }) {
      return getFieldData(this.fieldList(category), id);
    },
    getFieldValue(value, id) {
      let returnValue;
      if (value !== undefined && value.length > 0) {
        const fieldData = getFieldData(this.fieldList(this.category), id);
        if (fieldData) {
          let configKey = value.charAt(0).toUpperCase() + value.slice(1);
          returnValue = fieldData[configKey];
        }
      }
      if (returnValue === undefined) {
        returnValue =
          fieldMap[this.category] &&
          fieldMap[this.category][id] &&
          fieldMap[this.category][id][value];
      }
      return returnValue;
    },
    toggleCheckbox() {
      const isChecked = this.checkboxState.checked;
      const fields = this.fieldsInCard;
      fields.map((field) => {
        let parentField;
        let fieldId = field.id;
        let category = field.category;
        if (!field.id) {
          parentField = Object.keys(field)[0];
          fieldId = field[parentField].id;
          category = field[parentField].category;
        }
        const fieldData = this.getFieldData({
          id: fieldId,
          category: category || this.category,
        });
        isChecked
          ? this.unsetCheckbox({
              id: fieldData.FieldId,
              category: category || this.category,
              parentField,
            })
          : this.setCheckbox({
              id: fieldData.FieldId,
              category: category || this.category,
              type: fieldData.Type,
              parentField,
            });
      });
    },
    setCheckbox({ id, category, parentField, type }) {
      const defaultValue =
        this.getFieldValue({
          value: "defaultValue",
          category,
          id,
        }) || defaultValueFromType(type);

      let fieldId;
      let configValue = defaultValue;

      if (parentField !== undefined) {
        fieldId = parentField;
        configValue = { [id]: defaultValue };
      } else {
        fieldId = id;
      }
      this.$store.dispatch("setConfigValue", {
        ClientInfoRuleId: "bulkDelete",
        category,
        fieldId,
        value: configValue,
      });
    },
    unsetCheckbox({ id, category, parentField }) {
      this.$store.dispatch("unsetConfigValue", {
        ClientInfoRuleId: "bulkDelete",
        category,
        fieldId: id,
        parentField,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.card-title {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 12px 16px;
  background: $qvo-color-blue-400;
  border-radius: 8px 8px 0 0;
  align-items: center;
  gap: 36px;
  p {
    color: $qvo-color-grey-000;
  }
  .question {
    display: flex;
    align-items: center;
    gap: $qvo-spacing-8;
    .v-input--checkbox,
    .v-input--is-label-active {
      &::v-deep {
        .v-input__control .v-input--selection-controls__input {
          .mdi-checkbox-blank-outline,
          .mdi-minus-box-outline,
          .mdi-checkbox-marked {
            color: $qvo-color-grey-000;
          }
        }
      }
    }
    &::v-deep {
      .v-input__control
        .v-input__slot
        .v-input--selection-controls__input
        .v-icon {
        color: $qvo-color-grey-000;
      }
    }
  }
}
.content {
  border: 1px solid $qvo-color-grey-200;
  border-radius: 0 0 8px 8px;
}
</style>